import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { ChevronDown } from 'lucide-react';
import type { ApiError } from '@pms/shared';
import { requirementsService, type RequirementListItem, type RequirementStatus } from '@/services/requirements.service';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

function StatusDot({ color }: { color: string }) {
  return <span className="inline-block h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: color }} />;
}

interface Props {
  requirement: RequirementListItem;
}

export function RequirementStatusDropdown({ requirement }: Props) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data: statuses = [] } = useQuery<RequirementStatus[]>({
    queryKey: ['requirements', 'statuses'],
    queryFn: () => requirementsService.listStatuses(),
  });

  const mutation = useMutation({
    mutationFn: (statusId: string) =>
      requirementsService.updateRequirementStatus(requirement.id, statusId),
    onSuccess: () => {
      toast.success('Status updated');
      void queryClient.invalidateQueries({ queryKey: ['requirements', 'list'] });
      void queryClient.invalidateQueries({ queryKey: ['requirements', 'detail', requirement.id] });
      setOpen(false);
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const current = requirement.status;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-7 gap-1.5 px-2 text-xs font-medium border-white/30 bg-white/40 backdrop-blur-sm"
        >
          {current ? (
            <>
              <StatusDot color={current.color} />
              <span>{current.name}</span>
            </>
          ) : (
            <span className="text-muted-foreground">No status</span>
          )}
          <ChevronDown className="h-3 w-3 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-44 p-1" align="start">
        {statuses.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => mutation.mutate(s.id)}
            disabled={mutation.isPending || s.id === current?.id}
            className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent disabled:opacity-50"
          >
            <StatusDot color={s.color} />
            {s.name}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
